import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type {
  ApiResponse,
  BvAuditEvent,
  BvAuditEventInput,
  BvAuditEntityType,
} from '../types/billValidationTypes';

// Append-only history for bill validation (migration 066). Every write path that
// changes a number a client could later dispute — manual overrides, recalcs,
// validation outcomes — records what happened and who did it.

const SELECT_COLS =
  'id, org_id, entity_type, entity_id, site_id, action, field, old_value, new_value, ' +
  'note, actor_id, actor_email, assigned_to, resolved_at, resolved_by, created_at, ' +
  'assignee:assigned_to(id, email, full_name)';

type Actor = { id: string; email: string | null; orgId: string | null };

async function currentActor(): Promise<Actor | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .single();
  return { id: user.id, email: user.email ?? null, orgId: data?.organisation_id ?? null };
}

export const auditService = {
  /**
   * Record an event.
   *
   * Deliberately never throws: an audit write failing must not take down the
   * user's actual edit. Failures are logged and returned, and callers are free
   * to ignore the result.
   */
  log: async (input: BvAuditEventInput): Promise<ApiResponse<BvAuditEvent | null>> => {
    try {
      const actor = await currentActor();
      if (!actor?.orgId) return { success: false, error: 'Organization not found' };

      const { data, error } = await supabase
        .from('bv_audit_event')
        .insert({
          org_id:      actor.orgId,
          entity_type: input.entity_type,
          entity_id:   input.entity_id,
          site_id:     input.site_id ?? null,
          action:      input.action,
          field:       input.field ?? null,
          old_value:   input.old_value ?? null,
          new_value:   input.new_value ?? null,
          note:        input.note ?? null,
          assigned_to: input.assigned_to ?? null,
          actor_id:    actor.id,
          actor_email: actor.email,
        })
        .select(SELECT_COLS)
        .single();

      if (error) throw error;
      return { success: true, data: data as unknown as BvAuditEvent };
    } catch (error: unknown) {
      console.error('[auditService.log] failed:', error);
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Log a batch of field-level changes as one call. Fields whose value didn't
   * actually change are dropped — an audit trail full of no-op rows is noise
   * that hides the real edits.
   */
  logFieldChanges: async (
    base: Omit<BvAuditEventInput, 'field' | 'old_value' | 'new_value' | 'action'>,
    before: Record<string, unknown>,
    after: Record<string, unknown>,
    options?: { action?: BvAuditEventInput['action'] },
  ): Promise<ApiResponse<number>> => {
    try {
      const actor = await currentActor();
      if (!actor?.orgId) return { success: false, error: 'Organization not found' };

      const changed = Object.keys(after).filter(
        (key) => JSON.stringify(before[key] ?? null) !== JSON.stringify(after[key] ?? null),
      );
      if (changed.length === 0) return { success: true, data: 0 };

      const rows = changed.map((field) => ({
        org_id:      actor.orgId,
        entity_type: base.entity_type,
        entity_id:   base.entity_id,
        site_id:     base.site_id ?? null,
        action:      options?.action ?? 'updated',
        field,
        old_value:   before[field] ?? null,
        new_value:   after[field] ?? null,
        note:        base.note ?? null,
        assigned_to: base.assigned_to ?? null,
        actor_id:    actor.id,
        actor_email: actor.email,
      }));

      const { error } = await supabase.from('bv_audit_event').insert(rows);
      if (error) throw error;
      return { success: true, data: rows.length };
    } catch (error: unknown) {
      console.error('[auditService.logFieldChanges] failed:', error);
      return { success: false, error: (error as Error).message };
    }
  },

  /** History for one entity, newest first. */
  list: async (
    entityType: BvAuditEntityType,
    entityId: string,
    limit = 100,
  ): Promise<ApiResponse<BvAuditEvent[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_audit_event')
        .select(SELECT_COLS)
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return { success: true, data: (data ?? []) as unknown as BvAuditEvent[] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Everything recorded against a site *and* its bills/contracts/invoices.
   * This is what the site page's History tab shows — a user asking "what
   * happened to this site" doesn't think in terms of which table it landed in.
   */
  listForSite: async (siteId: string, limit = 200): Promise<ApiResponse<BvAuditEvent[]>> => {
    try {
      // A client edit isn't site-scoped (a client owns many sites), so pull the
      // site's owning client in and surface its organisation events here too —
      // "what happened to this site" includes "its client's details changed".
      const { data: siteRow } = await supabase
        .from('site').select('client_id').eq('id', siteId).single();
      const clientId = (siteRow as { client_id?: string } | null)?.client_id ?? null;

      const ors = [
        `site_id.eq.${siteId}`,
        `and(entity_type.eq.site,entity_id.eq.${siteId})`,
      ];
      if (clientId) ors.push(`and(entity_type.eq.organisation,entity_id.eq.${clientId})`);

      const { data, error } = await supabase
        .from('bv_audit_event')
        .select(SELECT_COLS)
        .or(ors.join(','))
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return { success: true, data: (data ?? []) as unknown as BvAuditEvent[] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Open follow-ups assigned to the signed-in user. */
  listMyOpenAssignments: async (limit = 50): Promise<ApiResponse<BvAuditEvent[]>> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: 'Not signed in' };

      const { data, error } = await supabase
        .from('bv_audit_event')
        .select(SELECT_COLS)
        .eq('assigned_to', user.id)
        .is('resolved_at', null)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return { success: true, data: (data ?? []) as unknown as BvAuditEvent[] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Org members, for the "assign this to…" picker. Returns an empty list rather
   * than an error when RLS hides the membership table — an unassignable note is
   * still a useful note.
   */
  listOrgMembers: async (): Promise<ApiResponse<Array<{ id: string; label: string }>>> => {
    try {
      const actor = await currentActor();
      if (!actor?.orgId) return { success: true, data: [] };

      const { data: members } = await supabase
        .from('base_organisation_members')
        .select('user_id')
        .eq('organisation_id', actor.orgId);

      const ids = (members ?? []).map((m: { user_id: string }) => m.user_id);
      if (ids.length === 0) return { success: true, data: [] };

      const { data: profiles } = await supabase
        .from('base_profiles')
        .select('id, email, full_name, name')
        .in('id', ids);

      const rows = (profiles ?? []).map((p: { id: string; email: string | null; full_name: string | null; name: string | null }) => ({
        id: p.id,
        label: p.full_name || p.name || p.email || p.id,
      }));
      return { success: true, data: rows.sort((a, b) => a.label.localeCompare(b.label)) };
    } catch (error: unknown) {
      console.error('[auditService.listOrgMembers] failed:', error);
      return { success: true, data: [] };
    }
  },

  /** Close out an assigned follow-up. RLS allows this and nothing else. */
  resolve: async (eventId: string): Promise<ApiResponse<null>> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: 'Not signed in' };

      const { error } = await supabase
        .from('bv_audit_event')
        .update({ resolved_at: new Date().toISOString(), resolved_by: user.id })
        .eq('id', eventId);
      if (error) throw error;
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};
